// =============================================
// LISTA DE CUENTAS BANCARIAS - ADMIN
// Gestión completa de cuentas bancarias
// =============================================

import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    CircularProgress,
    Alert,
    Snackbar,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import bankAccountService from '../../services/bankAccountService';
import BankAccountForm from './BankAccountForm';

const BankAccountList = () => {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Filtros
    const [yearFilter, setYearFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Cargar cuentas al montar
    useEffect(() => {
        loadBankAccounts();
    }, [yearFilter, statusFilter]);

    const loadBankAccounts = async () => {
        try {
            setLoading(true);
            const filters = {};
            if (yearFilter) filters.year = yearFilter;
            if (statusFilter !== 'all') filters.is_active = statusFilter === 'active';

            const response = await bankAccountService.getAll(filters);
            setBankAccounts(response.bankAccounts || []);
        } catch (error) {
            console.error('Error al cargar cuentas:', error);
            setSnackbar({
                open: true,
                message: 'Error al cargar cuentas bancarias',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedAccount(null);
        setOpenForm(true);
    };

    const handleEdit = (account) => {
        setSelectedAccount(account);
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setSelectedAccount(null);
    };

    const handleSuccess = () => {
        loadBankAccounts();
        setSnackbar({
            open: true,
            message: selectedAccount ? 'Cuenta actualizada exitosamente' : 'Cuenta creada exitosamente',
            severity: 'success',
        });
    };

    const handleDelete = async (account) => {
        if (window.confirm(`¿Estás seguro de desactivar la cuenta "${account.account_name}"?`)) {
            try {
                await bankAccountService.delete(account.id);
                loadBankAccounts();
                setSnackbar({
                    open: true,
                    message: 'Cuenta desactivada exitosamente',
                    severity: 'success',
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || 'Error al desactivar cuenta',
                    severity: 'error',
                });
            }
        }
    };

    // Obtener años únicos para el filtro
    const uniqueYears = [...new Set(bankAccounts.map(a => a.year))].sort((a, b) => b - a);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Encabezado */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Información Bancaria
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Nueva Cuenta
                </Button>
            </Box>

            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Año</InputLabel>
                        <Select
                            value={yearFilter}
                            label="Año"
                            onChange={(e) => setYearFilter(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {uniqueYears.map((year) => (
                                <MenuItem key={year} value={year}>
                                    {year}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Estado</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Estado"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="active">Activos</MenuItem>
                            <MenuItem value="inactive">Inactivos</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* Loading */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Tabla */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Año</TableCell>
                                    <TableCell>Banco</TableCell>
                                    <TableCell>Nombre de la Cuenta</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Número de Cuenta</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell align="center">Estado</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bankAccounts.map((account) => (
                                    <TableRow key={account.id} hover>
                                        <TableCell>{account.year}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="500">
                                                {account.bank_name || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {account.account_name}
                                            </Typography>
                                            {account.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {account.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={account.account_type}
                                                size="small"
                                                color={account.account_type === 'Corriente' ? 'primary' : 'secondary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {account.account_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {account.email ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{account.email}</Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Sin email
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={account.is_active ? 'Activo' : 'Inactivo'}
                                                color={account.is_active ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(account)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {account.is_active && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(account)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Sin datos */}
                    {bankAccounts.length === 0 && (
                        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No hay cuentas bancarias registradas
                            </Typography>
                        </Paper>
                    )}
                </>
            )}

            {/* Formulario */}
            <BankAccountForm
                open={openForm}
                onClose={handleCloseForm}
                account={selectedAccount}
                onSuccess={handleSuccess}
            />

            {/* Notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default BankAccountList;
